import {UserModel} from '../modules/auth/models/UserModel'
import {COUNTRY_CODES, USER_TYPE, USER_VERIFY_STATUS} from '../modules/profile/constants'
import {CurrencyCode} from '../pages/pricing/pricing.constant'
import {PAYMENT_METHOD} from './constants'

export const getUserFullName = (
  user: {
    firstName: string
    lastName: string
  },
  language: string
): string => {
  if (language === 'ja') {
    return `${user.lastName} ${user.firstName}`
  }
  return `${user.firstName} ${user.lastName}`
}

export const displayUseExchangeRate = (rate: number | undefined) => {
  if (rate === undefined) return 0
  return rate.toFixed(rate % 1 === 0 ? 0 : 2)
}

export function formatNumber(num: number): string {
  return num % 1 === 0 ? num.toString() : num.toFixed(2)
}
export function formatNumberWithCommas(num: number): string {
  const isInteger = num % 1 === 0
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: isInteger ? 0 : 2,
  }).format(num)
}
/**
 * Cleans up pagination params before sending to API
 * - Removes meta fields (itemCount, pageCount, hasPreviousPage, hasNextPage, currentPage)
 * - Removes null, undefined, and empty string values
 * @param params - The pagination params object
 * @returns Cleaned params with only page, take, and filter fields
 */
export function cleanupPaginationParams<T extends Record<string, any>>(params: T): Partial<T> {
  const ignoreKeys = ['itemCount', 'hasPreviousPage', 'hasNextPage', 'pageCount', 'currentPage']
  const cleanedParams: Record<string, any> = {}
  
  Object.entries(params).forEach(([key, value]) => {
    // Skip meta fields
    if (ignoreKeys.includes(key)) return
    // Skip null, undefined, and empty strings
    if (value === null || value === undefined || value === '') return
    cleanedParams[key] = value
  })
  
  return cleanedParams as Partial<T>
}

export interface PaymentMethodOption {
  icon: string
  name: PAYMENT_METHOD
  currency: CurrencyCode[]
  disabled: boolean
}

/**
 * Returns available payment methods for a user based on their verification status,
 * country, user type, and the payment currency.
 *
 * @param user - The user object containing verification and type info
 * @param currency - The currency for the payment
 * @param isFiatOnly - Whether to include credit payment method (default: false)
 * @returns Array of payment method options with disabled state
 */
export function getAvailablePaymentMethods(
  user: UserModel | null | undefined,
  currency: CurrencyCode,
  isFiatOnly: boolean = true
): PaymentMethodOption[] {
  const methods: PaymentMethodOption[] = []

  // Credit payment method (used in download orders)
  if (!isFiatOnly) {
    methods.push({
      icon: 'bi-wallet2',
      name: PAYMENT_METHOD.CREDIT,
      currency: [CurrencyCode.USD, CurrencyCode.JPY],
      disabled: false,
    })
  } else {
    const isBankEnabled =
      user &&
      user.verifyStatus === USER_VERIFY_STATUS.VERIFIED &&
      user.countryCode === COUNTRY_CODES.JP &&
      user.userType === USER_TYPE.ENTERPRISE &&
      currency === CurrencyCode.JPY

    // Visa/Stripe payment method - always available
    methods.push({
      icon: 'bi-credit-card',
      name: PAYMENT_METHOD.VISA,
      currency: [CurrencyCode.USD, CurrencyCode.JPY],
      disabled: isBankEnabled ? true : false,
    })

    // Bank transfer payment method - only for verified JP enterprise users with JPY currency

    methods.push({
      icon: 'bi-bank',
      name: PAYMENT_METHOD.BANK,
      currency: [CurrencyCode.JPY],
      disabled: !isBankEnabled,
    })
  }

  return methods
}
